import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import {TylerMatTableDataSource} from './angular-material/extended.class';
import {formatDate} from '@angular/common';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

@Injectable()
export class ExportService {

    public exportTableData(
        tableData: TylerMatTableDataSource,
        columns: string[],
        fileName: string,
        headerMutator: Function = null,
        columnMutator: Function = null,
        typeCoercion: Function = null,
        useFilterDataAccessor = false
    ) {
        // PRE: the table data should include all the data you want to print. The sorting data accessor is used to obtain the data.
        //      the columns you wish to display. The column names *must* match the headers of the columns.
        //      the name of the file
        // POST: prints all the columns in the order provide
        // NOTE: optionally, you can include a header mutator to change the header in the XLS based on the column header.
        //          it must take a string and return a string
        const exportData: any[] = [];
        const exportedData = tableData.sortData(tableData.filteredData, tableData.sort);
        exportedData.forEach(dataRow => {
            const exportRow: any[] = [];
            columns.forEach(column => {
                let columnToCheck = column;
                if (columnMutator && columnMutator(column)) {
                    columnToCheck = columnMutator(column);
                }
                let header = column;
                if (headerMutator && headerMutator(column)) {
                    header = headerMutator(column);
                }
                let type: string = typeof (dataRow[columnToCheck]);
                if (dataRow[columnToCheck] instanceof Date) {
                    type = 'date';
                }
                if (typeCoercion && typeCoercion(column)) {
                    type = typeCoercion(column);
                }
                if (type == 'string') {
                    exportRow[header] = dataRow[columnToCheck];
                } else if (type.toLowerCase() == 'date') {
                    if (dataRow[columnToCheck]) {
                        exportRow[header] = formatDate(dataRow[columnToCheck], 'MM/dd/yyyy', 'en');
                    } else {
                        exportRow[header] = '';
                    }
                } else {
                    if (useFilterDataAccessor) {
                        exportRow[header] = tableData.filterDataAccessor(dataRow, columnToCheck);
                    } else {
                        exportRow[header] = tableData.sortingDataAccessor(dataRow, columnToCheck);
                    }
                }
            });
            exportData.push(exportRow);
        });
        this.exportToExcel(exportData, fileName);
    }

    public exportMatGridToExcel(data: any[], columns: string[], fileName: string, detailColumns?: string[], excludedColumns?: string[]) {
        //  data:               Angular Material table data
        //  columns:            Columns currently displayed in Material table (Must match data field names)
        //  filename:           Excel file name
        //  detailColumns:      Data to include in export that is not currently displayed in Material table (Optional)
        //  excludedColumns:    Data to exclude in export that is currently displayed in Material table     (Optional)

        //  List of Columns to include in excel file
        const exportColumns: string[] = [];

        //  Copy columns currently displayed in Material table
        columns.forEach( column => {
            exportColumns.push(column);
        });

        //  Add columns selected columns not currently displayed in main row of Material Table
        if (detailColumns != undefined) {
            detailColumns.forEach(detailColumn => {
                exportColumns.push(detailColumn);
            });
        }

        //  Remove any excluded columns from export (More, Action, etc.)
        if (excludedColumns != undefined) {
            excludedColumns.forEach( exColumn => {
                const index: number = exportColumns.indexOf(exColumn);
                if (index != -1) {
                    exportColumns.splice(index, 1);
                }
            });
        }

        //  Create excel row for each row in Material table data
        const exportData: any[] = [];
        data.forEach(dataRow => {
            const exportRow: any[] = [];
            exportColumns.forEach(column => {
                exportRow[column] = dataRow[column];
            });

            exportData.push(exportRow);
        });
        this.exportToExcel(exportData, fileName);
    }

    public exportToExcel(json: any[], excelFileName: string): void {
        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
        const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAs(excelBuffer, excelFileName, EXCEL_EXTENSION);
    }

    public saveAs(buffer: any, fileName: string, fileExt: string): void {
        const data: Blob = new Blob([buffer], {type: EXCEL_TYPE});
        FileSaver.saveAs(data, fileName + '_export_' + new  Date().getTime() + fileExt);
    }
}
